import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import { FormikForm } from "components/FormikForm/FormikForm";
import Modal from "components/DesignSystem/Modal/Modal";
import { StatusModal } from "components/DesignSystem/StatusModal/StatusModal";
import { Field, FieldInputProps } from "formik";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useModal } from "hooks/useModal";
import { SingleValue } from "react-select";
import {
  useGetForecastSettingsQuery,
  useDeleteForecastSettingsMutation,
} from "store/apis/reports";
import { ForecastSettings } from "types/Models/report";
import { NewAssumptionSetModal } from "./NewAssumptionSetModal";
import dayjs from "dayjs";
import {
  FILTER_DATE_INPUT_FORMAT,
  MONTH_FORMAT,
  QUARTER_FORMAT,
  YEAR_FORMAT,
  YYYY_MM_DD,
} from "constants/date";
import { PERIOD, PeriodType } from "constants/revenueMetrics";
import { useState } from "react";
import { EditPencil } from "components/icons/EditPencil";
import { TrashSimple } from "components/icons/TrashSimple";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";

export type ForecastToolbarFilter = {
  END_DATE: string;
  FORECAST_ENABLED: boolean;
  FORECAST_START: string;
  FORECAST_END: string;
  FORECAST_SET_ID: string | null;
  VIEW_BY?: PeriodType | null;
};

type ForecastToolbarUpdateFilter = (
  ...args:
    | ["FORECAST_SET_ID", string | null]
    | ["FORECAST_START", string]
    | ["FORECAST_END", string]
) => void;

type Props = {
  filter: ForecastToolbarFilter;
  updateFilter: ForecastToolbarUpdateFilter;
};

const MAX_FORECAST_PERIOD_YEARS = 5;

const pickerTypeProps: Record<PeriodType, object> = {
  TOTALS: {},
  MONTHLY: { showMonthYearPicker: true },
  QUARTERLY: { showQuarterYearPicker: true },
  YEARLY: { showYearPicker: true },
};

const pickerDateFormat: Record<PeriodType, string> = {
  TOTALS: FILTER_DATE_INPUT_FORMAT,
  MONTHLY: MONTH_FORMAT,
  QUARTERLY: QUARTER_FORMAT,
  YEARLY: YEAR_FORMAT,
};

export const ForecastToolbar = ({ filter, updateFilter }: Props) => {
  const entityId = useCurrentEntityId();
  const newSetModal = useModal();
  const editSetModal = useModal();
  const deleteConfirmModal = useModal();
  const { alertToast, successToast } = useToast();

  const [editingSet, setEditingSet] = useState<ForecastSettings | null>(null);
  const [deletingSetId, setDeletingSetId] = useState<string | null>(null);

  const { data: sets = [], isFetching } = useGetForecastSettingsQuery(
    { entityId },
    { skip: !entityId }
  );

  const [deleteSettings, { isLoading: isDeleting }] =
    useDeleteForecastSettingsMutation();

  const openDeleteConfirm = (e: React.MouseEvent, settingsId: string) => {
    e.stopPropagation();
    e.preventDefault();
    setDeletingSetId(settingsId);
    deleteConfirmModal.open();
  };

  const handleDeleteConfirmed = async () => {
    if (!deletingSetId) return;
    try {
      await deleteSettings({ entityId, settingsId: deletingSetId }).unwrap();
      successToast({ message: "Assumption set deleted" });
      if (filter.FORECAST_SET_ID === deletingSetId) {
        updateFilter("FORECAST_SET_ID", null);
      }
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ||
          "Cannot delete the only assumption set",
      });
    } finally {
      setDeletingSetId(null);
      deleteConfirmModal.close();
    }
  };

  const openEdit = (e: React.MouseEvent, set: ForecastSettings) => {
    e.stopPropagation();
    e.preventDefault();
    setEditingSet(set);
    editSetModal.open();
  };

  const setOptions: OptionData[] = sets.map((s) => ({
    value: s.uuid,
    label: (
      <div className="t-group/option t-flex t-items-center t-justify-between t-w-full t-gap-2">
        <ConditionalToolTip condition={s.name}>
          <span className="t-truncate t-flex-1 t-text-body-sm">{s.name}</span>
        </ConditionalToolTip>
        <div className="t-flex t-gap-0.5 t-shrink-0 t-opacity-0 group-hover/option:t-opacity-100">
          <Button
            customType="ghost_icon"
            size="xsmall"
            type="button"
            onMouseDown={(e) => openEdit(e, s)}
          >
            <EditPencil width={12} height={12} />
          </Button>
          {sets.length > 1 && (
            <Button
              customType="ghost_icon"
              size="xsmall"
              type="button"
              onMouseDown={(e) => openDeleteConfirm(e, s.uuid)}
            >
              <TrashSimple width={12} height={12} />
            </Button>
          )}
        </div>
      </div>
    ),
  }));

  const firstSet = sets[0];
  if (!isFetching && firstSet && !filter.FORECAST_SET_ID) {
    updateFilter("FORECAST_SET_ID", firstSet.uuid);
  }

  const activeOption =
    setOptions.find((o) => o.value === filter.FORECAST_SET_ID) ||
    setOptions[0] ||
    null;

  const handleSetCreated = (setting: ForecastSettings) => {
    updateFilter("FORECAST_SET_ID", setting.uuid);
    newSetModal.close();
  };

  const handleSetChange = (
    option: SingleValue<OptionData> | readonly OptionData[]
  ) => {
    const selected = Array.isArray(option)
      ? null
      : (option as SingleValue<OptionData>);
    if (selected?.value) updateFilter("FORECAST_SET_ID", selected.value);
  };

  const deletingSetName = sets.find((s) => s.uuid === deletingSetId)?.name;

  const viewBy = filter.VIEW_BY || PERIOD.TOTALS;
  const pickerProps = pickerTypeProps[viewBy];
  const dateFormat = pickerDateFormat[viewBy];

  const minForecastStart = filter.END_DATE
    ? dayjs(filter.END_DATE).add(1, "day").format(YYYY_MM_DD)
    : undefined;

  const forecastStartForMaxEnd = filter.FORECAST_START || minForecastStart;
  const maxForecastEnd = forecastStartForMaxEnd
    ? dayjs(forecastStartForMaxEnd)
        .add(MAX_FORECAST_PERIOD_YEARS, "year")
        .format(YYYY_MM_DD)
    : undefined;

  const formatForecastEnd = (date: Date): string => {
    if (viewBy === PERIOD.MONTHLY || viewBy === PERIOD.QUARTERLY) {
      return dayjs(date).endOf("month").format(YYYY_MM_DD);
    }
    if (viewBy === PERIOD.YEARLY) {
      return dayjs(date).endOf("year").format(YYYY_MM_DD);
    }
    return dayjs(date).format(YYYY_MM_DD);
  };

  if (!filter.FORECAST_ENABLED) return null;

  return (
    <>
      <FormikForm
        enableReinitialize
        initialValues={{
          FORECAST_START: filter.FORECAST_START,
          FORECAST_END: filter.FORECAST_END,
        }}
        onSubmit={() => {}}
      >
        <div className="t-flex t-items-center t-justify-between sm:t-flex-col sm:t-items-stretch sm:t-gap-3">
          <div className="t-flex t-items-center t-gap-2 sm:t-flex-wrap">
            <span className="t-text-body-sm t-text-text-30 t-font-medium sm:t-w-full">
              Forecast period :
            </span>
            <div className="t-w-36 sm:t-w-auto sm:t-flex-1 sm:t-min-w-0">
              <Field name="FORECAST_START">
                {({ field }: { field: FieldInputProps<string> }) => (
                  <DateInput
                    {...field}
                    {...pickerProps}
                    dateFormat={dateFormat}
                    label=""
                    placeholder="Start date"
                    customSize="small"
                    minDate={
                      minForecastStart ? new Date(minForecastStart) : undefined
                    }
                    maxDate={
                      filter.FORECAST_END
                        ? new Date(filter.FORECAST_END)
                        : undefined
                    }
                    onDateChange={(date) =>
                      updateFilter(
                        "FORECAST_START",
                        dayjs(date).format(YYYY_MM_DD)
                      )
                    }
                  />
                )}
              </Field>
            </div>

            <span className="t-text-body-sm t-text-text-30">to</span>

            <div className="t-w-36 sm:t-w-auto sm:t-flex-1 sm:t-min-w-0">
              <Field name="FORECAST_END">
                {({ field }: { field: FieldInputProps<string> }) => {
                  let forecastEndMinDate: Date | undefined;
                  if (filter.FORECAST_START)
                    forecastEndMinDate = new Date(filter.FORECAST_START);
                  else if (minForecastStart)
                    forecastEndMinDate = new Date(minForecastStart);

                  return (
                    <DateInput
                      {...field}
                      {...pickerProps}
                      dateFormat={dateFormat}
                      label=""
                      placeholder="End date"
                      customSize="small"
                      minDate={forecastEndMinDate}
                      maxDate={
                        maxForecastEnd ? new Date(maxForecastEnd) : undefined
                      }
                      onDateChange={(date) =>
                        updateFilter("FORECAST_END", formatForecastEnd(date))
                      }
                    />
                  );
                }}
              </Field>
            </div>
          </div>
          <div className="t-flex t-items-center t-gap-2">
            <span className="t-text-body-sm t-text-text-30 t-font-medium sm:t-shrink-0">
              Assumption Set :
            </span>
            <div className="t-w-52 sm:t-w-auto sm:t-flex-1 sm:t-min-w-0">
              <Combobox
                options={setOptions}
                value={activeOption}
                onChange={handleSetChange}
                size="small"
                isClearable={false}
                isLoading={isFetching}
                placeholder="Select assumption set"
                menuPortalTarget={document.body}
                actions={
                  <Button
                    customType="link"
                    size="small"
                    type="button"
                    onClick={newSetModal.open}
                  >
                    + New assumption set
                  </Button>
                }
              />
            </div>
          </div>
        </div>
      </FormikForm>

      <NewAssumptionSetModal
        isOpen={newSetModal.isOpen}
        onClose={newSetModal.close}
        onCreated={handleSetCreated}
      />

      <NewAssumptionSetModal
        isOpen={editSetModal.isOpen}
        onClose={() => {
          editSetModal.close();
          setEditingSet(null);
        }}
        editingSet={editingSet}
      />

      <Modal.Root
        open={deleteConfirmModal.isOpen}
        onOpenChange={(open) => !open && deleteConfirmModal.close()}
      >
        <Modal.Content useCustomOverlay>
          <StatusModal type="danger" title="Delete assumption set">
            <p className="t-text-body t-text-text-60 t-mt-3">
              Are you sure you want to delete{" "}
              <span className="t-font-medium t-text-text-100">
                {deletingSetName}
              </span>
              ? This action cannot be undone.
            </p>
          </StatusModal>
          <Modal.Footer className="t-flex t-justify-end t-gap-3">
            <Button
              customType="secondary"
              type="button"
              onClick={deleteConfirmModal.close}
            >
              Cancel
            </Button>
            <Button
              customType="danger"
              type="button"
              onClick={handleDeleteConfirmed}
              isLoading={isDeleting}
              disabled={isDeleting}
            >
              Delete
            </Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal.Root>
    </>
  );
};
